import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';
import { fetchSlashGolfSchedule, importSlashGolfTournaments } from '../adminApiService';
import './SlashGolfImportModal.css';

const SlashGolfImportModal = ({
  showModal,
  setShowModal,
  onImportComplete,
  isLoading
}) => {
  const [tournaments, setTournaments] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTournaments, setSelectedTournaments] = useState(new Set());

  useEffect(() => {
    if (showModal) {
      fetchSlashGolfTournaments();
    }
  }, [showModal]);

  const fetchSlashGolfTournaments = async () => {
    setIsFetching(true);
    setError(null);
    
    try {
      const scheduleData = await fetchSlashGolfSchedule();
      setTournaments(scheduleData.tournaments || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching SlashGolf tournaments:', err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedTournaments.size === tournaments.length) {
      setSelectedTournaments(new Set());
    } else {
      setSelectedTournaments(new Set(tournaments.map(t => t.tournId)));
    }
  };

  const handleSelectTournament = (tournId) => {
    const newSelected = new Set(selectedTournaments);
    if (newSelected.has(tournId)) {
      newSelected.delete(tournId);
    } else {
      newSelected.add(tournId);
    }
    setSelectedTournaments(newSelected);
  };

  const handleImport = async () => {
    try {
      const selectedTournamentData = tournaments.filter(t => selectedTournaments.has(t.tournId));
      const result = await importSlashGolfTournaments(selectedTournamentData);
      
      if (onImportComplete) {
        onImportComplete(result);
      }
      
      setShowModal(false);
    } catch (err) {
      console.error('Error importing tournaments:', err);
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!showModal) return null;

  return (
    <div className="slash-golf-modal-overlay">
      <div className="slash-golf-modal-content slashgolf-modal">
        <div className="slash-golf-modal-header">
          <h2 className="modal-title">SlashGolf Tournament Import</h2>
          <button 
            className="slash-golf-modal-close"
            onClick={() => setShowModal(false)}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {isFetching ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Fetching upcoming tournaments from SlashGolf API...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">❌ {error}</p>
              <Button onClick={fetchSlashGolfTournaments} className="retry-btn">
                🔄 Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="import-description">
                <p>
                  Found <strong>{tournaments.length}</strong> upcoming tournaments from SlashGolf API. 
                  Select the tournaments you want to import as draft tournaments.
                </p>
              </div>

              <div className="selection-controls">
                <Button 
                  onClick={handleSelectAll}
                  className="select-all-btn"
                >
                  {selectedTournaments.size === tournaments.length ? 'Deselect All' : 'Select All'}
                </Button>
                <span className="selection-count">
                  {selectedTournaments.size} of {tournaments.length} selected
                </span>
              </div>

              <div className="tournaments-table-container">
                <table className="tournaments-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Tournament Name</th>
                      <th>Format</th>
                      <th>Dates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tournaments.map((tournament) => (
                      <tr key={tournament.tournId}>
                        <td>
                          <input
                            type="checkbox"
                            className="tournament-checkbox"
                            checked={selectedTournaments.has(tournament.tournId)}
                            onChange={() => handleSelectTournament(tournament.tournId)}
                          />
                        </td>
                        <td className="tournament-name">
                          <strong>{tournament.name}</strong>
                        </td>
                        <td>
                          <span className="format-badge">
                            {tournament.format || 'N/A'}
                          </span>
                        </td>
                        <td className="tournament-dates">
                          <div className="date-info">
                            <div><strong>Start:</strong> {formatDate(tournament.date.start)}</div>
                            <div><strong>End:</strong> {formatDate(tournament.date.end)}</div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="import-notes">
                <h4>📝 Import Details:</h4>
                <ul>
                  <li>All tournaments will be created with <strong>DRAFT</strong> status</li>
                  <li>Players will be automatically imported and assigned to tournaments</li>
                  <li>Max players will be set based on the actual player count from the API</li>
                  <li>Tournament details (format, purse, venue) will be preserved</li>
                  <li>Existing tournaments with the same tournId will be skipped</li>
                  <li>New golfers will be created if they don't exist in the system</li>
                </ul>
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>
                  💡 <strong>Note:</strong> This import includes complete tournament data with players, unlike the PGA Tour import.
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-actions">
          <Button
            onClick={() => setShowModal(false)}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            className="confirm-btn"
            disabled={selectedTournaments.size === 0 || isLoading}
          >
            {isLoading ? 'Importing...' : `Import ${selectedTournaments.size} Tournament${selectedTournaments.size !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SlashGolfImportModal;
